// Achievement unlock notification toast
import React, { useEffect, useState } from 'react';
import { Achievement } from '../../services/progressionService';
import { colors } from '../../theme/designTokens';
import { triggerHaptic } from '../../services/capacitorService';

interface AchievementToastProps {
  achievement: Achievement | null;
  onClose: () => void;
}

const AchievementToast: React.FC<AchievementToastProps> = ({ achievement, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<'enter' | 'show' | 'exit'>('enter');

  useEffect(() => {
    if (achievement) {
      // Trigger haptic
      triggerHaptic('medium');

      // Animation sequence
      setIsVisible(true);
      setAnimationPhase('enter');

      setTimeout(() => setAnimationPhase('show'), 100);

      // Auto-hide after 4 seconds
      setTimeout(() => {
        setAnimationPhase('exit');
        setTimeout(() => {
          setIsVisible(false);
          onClose();
        }, 300);
      }, 4000);
    }
  }, [achievement, onClose]);

  if (!isVisible || !achievement) return null;

  const tierColors: Record<Achievement['tier'], { bg: string; border: string }> = {
    bronze: {
      bg: 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)',
      border: '#CD7F32',
    },
    silver: {
      bg: 'linear-gradient(135deg, #C0C0C0 0%, #808080 100%)',
      border: '#C0C0C0',
    },
    gold: {
      bg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
      border: '#FFD700',
    },
    platinum: {
      bg: 'linear-gradient(135deg, #E5E4E2 0%, #BCC6CC 100%)',
      border: '#E5E4E2',
    },
    diamond: {
      bg: 'linear-gradient(135deg, #B9F2FF 0%, #00CED1 100%)',
      border: '#B9F2FF',
    },
  };

  const tierColor = tierColors[achievement.tier];

  const getTransform = () => {
    switch (animationPhase) {
      case 'enter':
        return 'translateX(400px)';
      case 'show':
        return 'translateX(0)';
      case 'exit':
        return 'translateX(400px)';
      default:
        return 'translateX(0)';
    }
  };

  const toastStyle: React.CSSProperties = {
    position: 'fixed',
    top: '5rem',
    right: '1rem',
    maxWidth: '90vw',
    width: '350px',
    background: colors.background.card,
    border: `3px solid ${tierColor.border}`,
    borderRadius: '1rem',
    padding: '1rem',
    boxShadow: `0 8px 24px rgba(0,0,0,0.4), 0 0 30px ${tierColor.border}50`,
    zIndex: 10000,
    transform: getTransform(),
    opacity: animationPhase === 'show' ? 1 : 0,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    backdropFilter: 'blur(10px)',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.75rem',
  };

  const iconStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
  };

  const titleContainerStyle: React.CSSProperties = {
    flex: 1,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    background: tierColor.bg,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '0.25rem',
  };

  const nameStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: colors.text.primary,
    marginBottom: '0.25rem',
  };

  const descStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    color: colors.text.secondary,
    lineHeight: 1.4,
  };

  const xpBadgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.25rem 0.75rem',
    borderRadius: '1rem',
    background: colors.gradients.gold,
    color: 'white',
    fontSize: '0.75rem',
    fontWeight: 700,
    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
    marginTop: '0.5rem',
  };

  return (
    <div style={toastStyle} onClick={() => {
      setAnimationPhase('exit');
      setTimeout(() => {
        setIsVisible(false);
        onClose();
      }, 300);
    }}>
      {/* Animated background glow */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle at 50% 50%, ${tierColor.border}20, transparent)`,
          borderRadius: '1rem',
          animation: 'pulse 2s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={headerStyle}>
          <div style={iconStyle}>{achievement.icon}</div>
          <div style={titleContainerStyle}>
            <div style={labelStyle}>Achievement Unlocked!</div>
            <div style={nameStyle}>{achievement.name}</div>
          </div>
        </div>

        <div style={descStyle}>{achievement.description}</div>

        <div style={xpBadgeStyle}>
          <span>⭐</span>
          <span>+{achievement.xpReward} XP</span>
        </div>
      </div>

      {/* Progress bar showing time remaining */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: '3px',
          width: '100%',
          background: tierColor.bg,
          borderRadius: '0 0 1rem 1rem',
          animation: 'shrink 4s linear',
        }}
      />
    </div>
  );
};

export default AchievementToast;
